import styled from 'styled-components';

import {
   PatientDataAccessAuthorizationVisitCommand,
   PatientDataAccessInvalidAuthorizationCodeFault,
   PatientDataAccessP1UnavailableFault,
} from '@dreryk/egabinet-patient-access-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { Translate } from '@dreryk/react-common';
import { Button, ButtonVariant, InfoTooltip, StyledFlex } from '@dreryk/react-components';

import { prescriptionsHistoryDownloadTranslationKeys } from '../translations';

import {
   PatientDataAccessVerificationCodeFormBean,
   PatientDataAccessVerificationCodeFormController,
} from './PatientDataAccessVerificationCodeForm.types';

type UseVerifyCodeCommandOptions = {
   onIncorrectCodeFault: VoidFunction;
   onP1CommunicationFault: VoidFunction;
   onSuccess: VoidFunction;
   applicationId: string;
   visitId: number;
};

type VerifyCommandFault = PatientDataAccessInvalidAuthorizationCodeFault | PatientDataAccessP1UnavailableFault;

const useVerifyCodeCommand = (
   options: UseVerifyCodeCommandOptions
): UseCqrsCommandResult<void, PatientDataAccessVerificationCodeFormBean, VerifyCommandFault> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new PatientDataAccessAuthorizationVisitCommand();
         command.authorizationCode = parameters.code as string;
         command.applicationId = options.applicationId;
         command.visitId = options.visitId;
         return command;
      },
      {
         onSuccess: options.onSuccess,
         onFault: {
            'egabinet-patient-access-frontend-api:PatientDataAccessInvalidAuthorizationCodeFault':
               options.onIncorrectCodeFault,
            'egabinet-patient-access-frontend-api:PatientDataAccessP1UnavailableFault': () =>
               options.onP1CommunicationFault(),
         },
      }
   );
};

type PatientDataAccessVerificationCodeVerifyButtonProps = {
   readonly handleSubmit: PatientDataAccessVerificationCodeFormController['handleSubmit'];
   readonly onIncorrectCodeFault: VoidFunction;
   readonly onP1CommunicationFault: VoidFunction;
   readonly onSuccess: VoidFunction;
   readonly applicationId: string;
   readonly visitId: number;
};

const StyledButton = styled(Button)`
   align-self: flex-end;
`;

const StyledInfoTooltipIconWrapper = styled.span`
   position: absolute;
   top: 0;
   right: -23px;
`;
StyledInfoTooltipIconWrapper.displayName = 'StyledInfoTooltipIconWrapper';

export const PatientDataAccessVerificationCodeVerifyButton = (
   props: PatientDataAccessVerificationCodeVerifyButtonProps
): JSX.Element => {
   const { executeAsync: verifyCode } = useVerifyCodeCommand({
      onIncorrectCodeFault: props.onIncorrectCodeFault,
      onP1CommunicationFault: props.onP1CommunicationFault,
      onSuccess: props.onSuccess,
      applicationId: props.applicationId,
      visitId: props.visitId,
   });
   return (
      <StyledFlex direction={'column'} gap={'6px'}>
         <InfoTooltip
            testId={'patientCodeInfo'}
            content={
               <Translate>{prescriptionsHistoryDownloadTranslationKeys.codeIsInPatientPhoneNotification}</Translate>
            }
         />
         <StyledButton
            testId={'verify'}
            label={prescriptionsHistoryDownloadTranslationKeys.verify}
            variant={ButtonVariant.SECONDARY}
            onClick={props.handleSubmit(verifyCode)}
         />
      </StyledFlex>
   );
};
